import "@tradingdroid/coreui-pro/dist/css/coreui.min.css";
import "@tradingdroid/frontends-components/styles.css";
import "@src/scss/index.scss";

import React from "react";
import { Navigate, Route, Routes, useNavigate, useParams } from "react-router-dom";
import { CAlert, CSpinner } from "@coreui/react";
import { ConfirmModal } from "@src/components/ConfirmModal";
import { JobPollingManager } from "@src/components/JobPollingManager";
import { JsonModal } from "@src/components/JsonModal";
import { Layout } from "@src/components/Layout";
import { ToastManager } from "@src/components/ToastManager";
import { UnauthenticatedApp } from "@src/components/UnauthenticatedApp";
import { useIsPreloading, usePreloadData } from "@src/hooks/redux";
import { useAuth } from "@src/hooks/useAuth";
import { useConfirmModal } from "@src/hooks/useConfirmModal";
import { useJsonModal } from "@src/hooks/useJsonModal";
import { JobDetailPage } from "@src/pages/JobDetailPage";
import { JobsListPage } from "@src/pages/JobsListPage";
import { OptimizationsListPage } from "@src/pages/OptimizationsListPage";
import { OptimizeExecPage } from "@src/pages/OptimizeExecPage";
import { PlaygroundPage } from "@src/pages/PlaygroundPage";
import { PrototypingPage } from "@src/pages/PrototypingPage";
import { PortfolioPage } from "@src/pages/PortfolioPage";
import { PositionPage } from "@src/pages/PositionPage";
import { ExecGroupsListPage } from "@src/pages/ExecGroupsListPage";
import { ExecutionGroupDetailPage } from "@src/pages/ExecutionGroupDetailPage";
import { RunDetailPage } from "@src/pages/RunDetailPage";
import { RunRunnerPage } from "@src/pages/RunRunnerPage";
import { RunsListPage } from "@src/pages/RunListPage";

// A study used to live at /optimization/:id; keep old links working.
const StudyRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/study/${id}`} replace />;
};
import { UserSettingsPage } from "@src/pages/UserSettingsPage";
import type { User } from "@src/services/api/tdApi";
import { useInitializeApi } from "@src/services/api/tdApi";

export const App = () => {
  const navigate = useNavigate();

  // Initialize API first - everything depends on this
  const { data: apiData, isLoading: apiLoading, error: apiError } = useInitializeApi();

  // useAuth uses authService Proxy export (initialized by useInitializeApi)
  const { user, isAuthenticating, login, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  // Show error if API initialization failed
  if (apiError) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="text-center">
          <CAlert color="danger">
            <h4>Failed to initialize API</h4>
            <p>{apiError.message}</p>
          </CAlert>
        </div>
      </div>
    );
  }

  // Show loading spinner while initializing API or checking authentication
  if (apiLoading || !apiData || isAuthenticating) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="text-center">
          <CSpinner color="primary" style={{ width: "3rem", height: "3rem" }} />
          <p className="mt-3">{!apiData ? "Initializing API..." : "Checking authentication..."}</p>
        </div>
      </div>
    );
  }

  // Show login flow if not authenticated
  if (!user?.isAuthenticated) {
    return <UnauthenticatedApp login={login} />;
  }

  // User is authenticated - show main app
  return <AuthenticatedApp user={user} onLogout={handleLogout} />;
};

/**
 * AuthenticatedApp - API is already initialized, just handle data preloading
 */
const AuthenticatedApp: React.FC<{ user: User; onLogout: () => void }> = ({ user, onLogout }) => {
  // API already initialized in main App component
  // Just render the data loader and routes
  return <AuthedAppRouting user={user} onLogout={onLogout} />;
};

/**
 * DataLoader - Separate component to handle data preloading
 * This ensures hooks are called consistently
 */
const AuthedAppRouting: React.FC<{ user: User; onLogout: () => void }> = ({ user, onLogout }) => {
  // Now preload data - API is already initialized
  const { error: preloadError } = usePreloadData();
  const isPreloading = useIsPreloading();
  const { jsonModal, hideJsonModal } = useJsonModal();
  const { confirmModalData, resolveConfirm } = useConfirmModal();

  // Show loading while preloading data
  if (isPreloading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="text-center">
          <CSpinner color="primary" style={{ width: "3rem", height: "3rem" }} />
          <p className="mt-3">Loading data...</p>
        </div>
      </div>
    );
  }

  // Show preload error
  if (preloadError) {
    return (
      <CAlert color="danger">
        Error loading data:{" "}
        {preloadError instanceof Error ? preloadError.message : String(preloadError)}
      </CAlert>
    );
  }

  // Everything loaded - show app
  return (
    <>
      <Layout user={user} onLogout={onLogout}>
        <Routes>
          <Route path="/" element={<Navigate to="/executions" replace />} />
          <Route path="/portfolio" element={<PortfolioPage />} />

          <Route path="/executions" element={<RunsListPage key="executions" type="execution" />} />
          <Route path="/execution/new" element={<RunRunnerPage type="execution" />} />
          <Route path="/execution/:id" element={<RunDetailPage type="execution" />} />
          <Route path="/exec-groups" element={<ExecGroupsListPage />} />
          <Route path="/exec-group/:id" element={<ExecutionGroupDetailPage />} />

          <Route path="/backtests" element={<RunsListPage key="backtests" type="backtest" />} />
          <Route path="/backtest/new" element={<RunRunnerPage type="backtest" />} />
          <Route path="/backtest/:id" element={<RunDetailPage type="backtest" />} />

          <Route path="/studies" element={<OptimizationsListPage />} />
          <Route path="/optimizations" element={<Navigate to="/studies" replace />} />
          <Route path="/optimization/new" element={<OptimizeExecPage />} />
          <Route path="/study/:id" element={<OptimizeExecPage />} />
          <Route path="/optimization/:id" element={<StudyRedirect />} />

          <Route path="/jobs" element={<JobsListPage />} />
          <Route path="/job/:id" element={<JobDetailPage />} />

          {/* A position on a chart, stored or not. ":id" reads one back from the platform;
              "virtual" draws trades that live only in the link, for a screener or paper book
              that decides trades without storing them. See utils/virtualRun.ts for the payload. */}
          <Route path="/position/virtual" element={<PositionPage />} />
          <Route path="/position/:id" element={<PositionPage />} />

          <Route path="/playground" element={<PlaygroundPage />} />
          <Route path="/prototyping" element={<PrototypingPage />} />

          <Route path="/settings" element={<UserSettingsPage />} />

          <Route path="*" element={<Navigate to="/executions" replace />} />
        </Routes>
      </Layout>

      <ToastManager />

      {jsonModal && (
        <JsonModal
          title={jsonModal.title}
          data={jsonModal.data}
          actions={jsonModal.actions}
          onClose={hideJsonModal}
        />
      )}

      {confirmModalData && <ConfirmModal data={confirmModalData} onConfirm={resolveConfirm} />}

      <JobPollingManager />
    </>
  );
};
